/**
 * rum.ts — RUM старта мини-апа. Публичный fire-and-forget beacon → mobile-gw
 * `POST /v1/miniapp/rum` (без auth: сигнал летит ДО авторизации). Ловим боль
 * «совсем не открылось / долго открывалось». Смотреть: `padla look miniapp`.
 * План: docs/superpowers/plans/2026-07-01-miniapp-connectivity-observability.md
 *
 * boot_started шлётся inline-скриптом в index.html ДО бандла (index долетел, JS
 * пошёл). app_boot — отсюда, когда приложение реально пригодно (первый осевший
 * запрос данных): ловит и «шелл есть, но /api завис» (firstApiOk=false).
 */

export interface AppBootPayload {
  bundleMs?: number;
  firstApiMs?: number;
  firstApiOk?: boolean;
  retries?: number;
}

type FetchLike = typeof fetch;

/** Низкоуровневый посыл — никогда не бросает, никогда не блокирует UX (П5). */
export function sendRum(
  body: Record<string, unknown>,
  apiBase = '/api',
  fetchImpl: FetchLike = fetch,
): void {
  try {
    void fetchImpl(`${apiBase}/v1/miniapp/rum`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* сбор метрик не критичен */
  }
}

/** app_boot — полный старт (или частичный сбой первого /api). Один раз на загрузку. */
export function trackAppBoot(
  payload: AppBootPayload,
  apiBase = '/api',
  fetchImpl: FetchLike = fetch,
): void {
  sendRum({ type: 'app_boot', ...payload }, apiBase, fetchImpl);
}

export interface ClientErrorPayload {
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
}

const cut = (v: string | null | undefined, n: number): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v.slice(0, n) : undefined;

/**
 * Краш-репорт UI → mobile-gw `POST /v1/miniapp/client-error` (без auth: краш
 * бывает и до логина). Консоль Telegram-webview невидима — это единственный
 * канал, по которому рендер-краш мини-апа доезжает до логов прода
 * (evt=miniapp_client_error). Никогда не бросает (П5); keepalive — доходит,
 * даже если вкладка умирает.
 */
export function reportClientError(
  payload: ClientErrorPayload,
  apiBase = '/api',
  fetchImpl: FetchLike = fetch,
): void {
  try {
    void fetchImpl(`${apiBase}/v1/miniapp/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: cut(payload.message, 500) ?? 'unknown',
        stack: cut(payload.stack, 8000),
        componentStack: cut(payload.componentStack, 8000),
        url: cut(payload.url ?? window.location?.href, 300),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* репорт краша не должен добивать UX */
  }
}
